import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface AdviceItem {
  readonly adviceId: string;
  readonly claimNumber: string;
  readonly adviceNumber: string;
  readonly amount: number;
  readonly adviceDate: string;
  readonly paymentMode: string;
  readonly beneficiaryAccount: string;
  readonly beneficiaryIfsc: string;
  readonly status: string;
}

/** L15.10 — Vendor Payment Advices: download/view payment advice documents. */
export function VendorPaymentAdvicesPage(): ReactNode {
  const { token } = useAuth();
  const [items, setItems] = useState<readonly AdviceItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ items: readonly AdviceItem[] }>('/v1/vendor/payment-advices', { token })
      .then(r => { if (alive) setItems(r.items); })
      .catch(e => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  return (
    <div style={{ padding: 24 }}>
      <h1>Payment Advices</h1>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!items && !error && <p>Loading...</p>}
      {items && items.length === 0 && <p>No payment advices available.</p>}
      {items && items.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f6f8fa', borderBottom: '1px solid #d0d7de' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>Advice #</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Claim #</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Amount (₹)</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Mode</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Account</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Date</th>
              <th style={{ padding: 8 }}>Download</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a, i) => (
              <tr key={a.adviceId ?? i} style={{ borderBottom: '1px solid #d0d7de' }}>
                <td style={{ padding: 8, fontFamily: 'monospace' }}>{a.adviceNumber}</td>
                <td style={{ padding: 8 }}>{a.claimNumber}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{a.amount.toLocaleString('en-IN')}</td>
                <td style={{ padding: 8 }}>{a.paymentMode}</td>
                <td style={{ padding: 8, fontFamily: 'monospace' }}>{a.beneficiaryAccount?.replace(/.(?=.{4})/g, '*')}</td>
                <td style={{ padding: 8 }}>{a.adviceDate ? new Date(a.adviceDate).toLocaleDateString() : '—'}</td>
                <td style={{ padding: 8, textAlign: 'center' }}>
                  <a href={`/api/v1/vendor/payment-advices/${a.adviceId}/pdf`} target="_blank" rel="noopener noreferrer" style={{ color: '#0969da', textDecoration: 'none' }}>PDF</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
